// src/modules/estimates/rates/services/resourceRates.service.ts
import { supabase } from '@/core/database/supabase';
import { Resource, ResourceRate, Category, Supplier } from '../types';

export const resourceRatesService = {
  /**
   * Fetch resources with their current rates
   */
  async getResources(type?: string): Promise<Resource[]> {
    let query = supabase
      .from('resources')
      .select(`
        *,
        categories!inner(
          *,
          category_groups!inner(code)
        ),
        rates:rates(
          *,
          supplier:suppliers(*)
        )
      `)
      .eq('status', 'ACTIVE');

    if (type) {
      query = query.eq('categories.category_groups.code', type.toLowerCase());
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('[ResourceRatesService] Error fetching resources:', error);
      return [];
    }

    // Filter to get only the current rate for each resource
    return (data || []).map((res: any) => ({
      ...res,
      category: res.categories,
      current_rate: res.rates?.find((r: any) => r.status === 'CURRENT') || res.rates?.[0]
    })) as Resource[];
  },

  /**
   * Get specific resource with history
   */
  async getResourceDetail(id: string): Promise<Resource & { rates: ResourceRate[] }> {
    const { data, error } = await supabase
      .from('resources')
      .select(`
        *,
        categories(
          *,
          category_groups(code)
        ),
        rates:rates(
          *,
          supplier:suppliers(*)
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    return {
      ...data,
      category: (data as any).categories
    } as any;
  },

  /**
   * Upsert resource rate
   */
  async updateRate(rate: Partial<ResourceRate>) {
    // If updating, archive previous 'CURRENT' rate
    if (rate.resource_id) {
      await supabase
        .from('rates')
        .update({ status: 'ARCHIVED' })
        .eq('resource_id', rate.resource_id)
        .eq('status', 'CURRENT');
    }

    const { data, error } = await supabase
      .from('rates')
      .insert({
        ...rate,
        status: 'CURRENT',
        effective_from: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Search suppliers
   */
  async searchSuppliers(term: string): Promise<Supplier[]> {
    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .ilike('name', `%${term}%`)
      .limit(10);

    if (error) throw error;
    return data as Supplier[];
  },

  /**
   * Get overall stats for header
   */
  async getStats() {
    const { count: totalItems } = await supabase.from('resources').select('*', { count: 'exact', head: true });
    const { count: totalSuppliers } = await supabase.from('suppliers').select('*', { count: 'exact', head: true });
    
    return {
      totalItems: totalItems || 0,
      totalSuppliers: totalSuppliers || 0,
      todayUpdates: 0,
      pendingApprovals: 0
    };
  },

  /**
   * Get all categories filtered by type code from category_groups
   */
  async getCategories(type?: string): Promise<Category[]> {
    let query = supabase
      .from('categories')
      .select(`
        id,
        name,
        parent_id,
        path,
        level,
        group:category_groups!inner(code)
      `)
      .eq('is_active', true)
      .order('path', { ascending: true }); // Crucial for hierarchical display
    
    if (type) {
      query = query.eq('category_groups.code', type.toLowerCase());
    }

    const { data, error } = await query;
    if (error) {
      console.error('[ResourceRatesService] Error fetching categories:', error);
      return [];
    }

    return (data || []).map(row => ({
      id: row.id,
      name: row.name,
      parent_id: row.parent_id,
      level: row.level || 0,
      type: (row as any).group?.code?.toUpperCase() || 'OTHER'
    })) as any[];
  },

  /**
   * Get units from the "Đơn vị tính" category branch
   */
  async getUnits(): Promise<string[]> {
    // 1. Find the parent "Đơn vị tính" category
    const { data: parent } = await supabase
      .from('categories')
      .select('id')
      .ilike('name', '%Đơn vị tính%')
      .single();

    if (!parent) return ['m3', 'm2', 'kg', 'mét', 'cái']; // Fallback

    // 2. Fetch children
    const { data: children } = await supabase
      .from('categories')
      .select('name')
      .eq('parent_id', parent.id)
      .eq('is_active', true)
      .order('name');

    return (children || []).map(c => c.name);
  }
};
